import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";

const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;
const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js";

export const TURNSTILE_ENABLED = Boolean(SITE_KEY);

let scriptPromise = null;
const loadTurnstileScript = () => {
  if (window.turnstile) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Falha ao carregar verificação de segurança"));
    document.head.appendChild(script);
  });
  return scriptPromise;
};

// Verificação "não sou um robô" (Cloudflare Turnstile). Fica invisível (retorna null)
// quando REACT_APP_TURNSTILE_SITE_KEY não está configurada.
export const TurnstileWidget = forwardRef(({ onVerify, className = "" }, ref) => {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const onVerifyRef = useRef(onVerify);
  onVerifyRef.current = onVerify;

  useImperativeHandle(ref, () => ({
    reset: () => {
      if (window.turnstile && widgetIdRef.current !== null) {
        window.turnstile.reset(widgetIdRef.current);
      }
    },
  }));

  useEffect(() => {
    if (!SITE_KEY) return undefined;
    let cancelled = false;
    loadTurnstileScript()
      .then(() => {
        if (cancelled || !containerRef.current || !window.turnstile) return;
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: SITE_KEY,
          callback: (token) => onVerifyRef.current?.(token),
          "expired-callback": () => onVerifyRef.current?.(""),
          "error-callback": () => onVerifyRef.current?.(""),
        });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      if (window.turnstile && widgetIdRef.current !== null) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, []);

  if (!SITE_KEY) return null;
  return <div ref={containerRef} className={className} />;
});

TurnstileWidget.displayName = "TurnstileWidget";
